/**
 * Create Email Account Modal Component
 *
 * Modal with form for creating a new OAuth2 email account.
 * Includes validation and automatic OAuth2 flow initiation.
 */

import {
  Modal,
  ModalProps,
  TextInput,
  Select,
  Button,
  Stack,
  Group,
  NumberInput,
  Text,
  Alert,
  CopyButton,
  ActionIcon,
  Tooltip
} from "@mantine/core";
import { useForm, zodResolver } from "@mantine/form";
import { IconInfoCircle, IconCopy, IconCheck } from "@tabler/icons-react";
import { useState, useEffect, useCallback } from "react";
import { z } from "zod";
import type { EmailProvider, EmailAccountFormData } from "../../schema/email-account.types";

/**
 * Form validation schema
 */
const emailAccountSchema = z.object({
  email: z.string().email("Email inválido").optional().or(z.literal("")),
  display_name: z.string().min(1, "Nome de exibição é obrigatório"),
  provider: z.enum(["gmail", "outlook", "custom"], {
    errorMap: () => ({ message: "Provider é obrigatório" })
  }),
  oauth_config: z.object({
    client_id: z.string().min(1, "Client ID é obrigatório"),
    client_secret: z.string().min(1, "Client Secret é obrigatório"),
    scopes: z.string().min(1, "Scopes são obrigatórios"),
    smtp_host: z.string().optional(),
    smtp_port: z.number().optional()
  })
});

type FormValues = z.infer<typeof emailAccountSchema>;

/**
 * Props for CreateEmailAccountModal
 */
interface CreateEmailAccountModalProperties extends Omit<ModalProps, "onClose"> {
  /** Whether modal is open */
  opened: boolean;
  /** Callback when modal is closed */
  onClose: () => void;
  /** Callback when account is created (receives account ID) */
  onCreate: (data: EmailAccountFormData) => Promise<string>;
  /** Loading state during creation */
  loading?: boolean;
}

/**
 * Provider options for select
 */
const PROVIDER_OPTIONS = [
  { value: "gmail", label: "Gmail" },
  { value: "outlook", label: "Outlook" },
  { value: "custom", label: "Custom SMTP" }
];

/**
 * Default scopes by provider
 */
/**
 * Default OAuth2 scopes by provider
 *
 * IMPORTANTE: Para Gmail SMTP, use https://mail.google.com/ (não gmail.send)
 * - https://mail.google.com/ = Acesso completo SMTP/IMAP (OBRIGATÓRIO para SMTP)
 * - https://www.googleapis.com/auth/gmail.send = Apenas Gmail HTTP API (NÃO funciona com SMTP)
 *
 * Scopes adicionais necessários:
 * - email = Acesso ao endereço de email do usuário (para auto-preencher)
 * - openid = OpenID Connect (recomendado para OAuth2)
 */
const DEFAULT_SCOPES: Record<EmailProvider, string> = {
  gmail: "https://mail.google.com/ email openid",
  outlook: "https://outlook.office365.com/SMTP.Send email openid",
  custom: ""
};

/**
 * CreateEmailAccountModal Component
 * @param root0
 * @param root0.opened
 * @param root0.onClose
 * @param root0.onCreate
 * @param root0.loading
 * @param root0.modalProperties
 * @example
 * <CreateEmailAccountModal
 *   opened={opened}
 *   onClose={() => setOpened(false)}
 *   onCreate={async (data) => {
 *     const accountId = await createAccount(data);
 *     return accountId;
 *   }}
 * />
 */
export function CreateEmailAccountModal({
  opened,
  onClose,
  onCreate,
  loading = false,
  ...modalProperties
}: CreateEmailAccountModalProperties) {
  const [submitting, setSubmitting] = useState(false);
  const [redirectUri, setRedirectUri] = useState<string | null>(null);
  const [loadingRedirectUri, setLoadingRedirectUri] = useState(false);
  const [redirectUriError, setRedirectUriError] = useState<string | null>(null);

  // Fetch OAuth2 redirect URI when modal opens
  useEffect(() => {
    if (opened && !redirectUri) {
      const fetchRedirectUri = async () => {
        try {
          setLoadingRedirectUri(true);
          setRedirectUriError(null);
          const { getOAuth2RedirectUri } = await import("../../functions");
          const uri = await getOAuth2RedirectUri();
          setRedirectUri(uri);
        } catch (error) {
          console.error("Failed to fetch redirect URI:", error);
          const errorMessage = error instanceof Error ? error.message : String(error);
          setRedirectUri("Erro ao carregar URL");
          setRedirectUriError(errorMessage);
        } finally {
          setLoadingRedirectUri(false);
        }
      };

      void fetchRedirectUri();
    }
  }, [opened, redirectUri]);

  const form = useForm<FormValues>({
    validate: zodResolver(emailAccountSchema),
    initialValues: {
      email: "",
      display_name: "",
      provider: "gmail",
      oauth_config: {
        client_id: "",
        client_secret: "",
        scopes: DEFAULT_SCOPES.gmail,
        smtp_host: "",
        smtp_port: undefined
      }
    }
  });

  const isCustomProvider = form.values.provider === "custom";

  /**
   * Handle provider change - update default scopes
   * @param value
   */
  const handleProviderChange = useCallback(
    (value: string | null) => {
      if (!value) return;

      const provider = value as EmailProvider;
      form.setFieldValue("provider", provider);
      form.setFieldValue("oauth_config.scopes", DEFAULT_SCOPES[provider]);
    },
    [form]
  );

  /**
   * Handle form submission
   * @param values
   */
  const handleSubmit = async (values: FormValues) => {
    try {
      setSubmitting(true);

      // Transform form data to EmailAccountFormData
      const formData: EmailAccountFormData = {
        email: values.email || undefined, // Send undefined if empty (will use placeholder in backend)
        display_name: values.display_name,
        provider: values.provider,
        oauth_config: {
          client_id: values.oauth_config.client_id,
          client_secret: values.oauth_config.client_secret,
          scopes: values.oauth_config.scopes.split(",").map((s) => s.trim()),
          smtp_host: values.oauth_config.smtp_host,
          smtp_port: values.oauth_config.smtp_port
        }
      };

      await onCreate(formData);

      // Reset form and close
      form.reset();
      onClose();
    } catch (error) {
      console.error("Failed to create email account:", error);
    } finally {
      setSubmitting(false);
    }
  };

  /**
   * Handle modal close
   */
  const handleClose = useCallback(() => {
    if (!submitting) {
      form.reset();
      onClose();
    }
  }, [submitting, form, onClose]);

  return (
    <Modal
      {...modalProperties}
      opened={opened}
      onClose={handleClose}
      title="Nova Conta de Email"
      size="lg"
      closeOnClickOutside={false}
    >
      <form onSubmit={form.onSubmit(handleSubmit)}>
        <Stack gap="md">
          {/* Info alert */}
          <Alert icon={<IconInfoCircle size={16} />} color="primary">
            Após criar a conta, você será redirecionado para autorizar o acesso via OAuth2. O email será verificado e
            preenchido automaticamente pelo provider OAuth2.
          </Alert>

          {/* Email input */}
          <TextInput
            label="Email"
            placeholder="Será preenchido automaticamente via OAuth2"
            description="Opcional - será verificado e atualizado automaticamente durante a autorização OAuth2"
            autoComplete="off"
            {...form.getInputProps("email")}
          />

          {/* Display name input */}
          <TextInput label="Nome de Exibição" required autoComplete="off" {...form.getInputProps("display_name")} />

          {/* Provider select */}
          <Select
            label="Provider"
            placeholder="Selecione o provider"
            data={PROVIDER_OPTIONS}
            required
            value={form.values.provider}
            onChange={handleProviderChange}
            error={form.errors.provider}
          />

          {/* Client ID input */}
          <TextInput
            label="OAuth2 Client ID"
            placeholder="123456789.apps.googleusercontent.com"
            required
            autoComplete="off"
            {...form.getInputProps("oauth_config.client_id")}
          />

          {/* Client Secret input */}
          <TextInput
            label="OAuth2 Client Secret"
            placeholder="GOCSPX-xxxxx"
            description="Secret do OAuth2 (será criptografado no servidor). NUNCA compartilhe este valor."
            required
            autoComplete="new-password"
            {...form.getInputProps("oauth_config.client_secret")}
          />

          {/* OAuth2 Redirect URI Alert */}
          <Alert icon={<IconInfoCircle size={16} />} color="primary" title="Configuração OAuth2 Redirect URI">
            <Stack gap="xs">
              <Text size="sm">
                Configure esta URL exata no console do seu provider OAuth2 (Google Cloud Console, Azure AD, etc.):
              </Text>
              {loadingRedirectUri ? (
                <Text size="sm" fw={500} c="dimmed">
                  Carregando URL...
                </Text>
              ) : (
                <Stack gap="xs">
                  <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    <Text size="sm" fw={500} style={{ fontFamily: "monospace", wordBreak: "break-all" }}>
                      {redirectUri}
                    </Text>
                    {redirectUri && redirectUri !== "Erro ao carregar URL" && (
                      <CopyButton value={redirectUri}>
                        {({ copied, copy }) => (
                          <Tooltip label={copied ? "Copiado!" : "Copiar"}>
                            <ActionIcon color={copied ? "success" : "primary"} onClick={copy} size="sm">
                              {copied ? <IconCheck size={16} /> : <IconCopy size={16} />}
                            </ActionIcon>
                          </Tooltip>
                        )}
                      </CopyButton>
                    )}
                  </div>
                  <Text size="xs" c="dimmed">
                    ⚠️ A URL deve corresponder EXATAMENTE (incluindo http/https, porta, e caminho completo)
                  </Text>
                  {redirectUriError && (
                    <Text size="xs" c="red">
                      Detalhe do erro: {redirectUriError}
                    </Text>
                  )}
                </Stack>
              )}
            </Stack>
          </Alert>

          {/* Scopes input */}
          <TextInput
            label="OAuth2 Scopes"
            placeholder="Scopes separados por vírgula"
            description="Escopos de permissão OAuth2 (separados por vírgula)"
            required
            {...form.getInputProps("oauth_config.scopes")}
          />

          {/* Custom SMTP fields (conditional) */}
          {isCustomProvider && (
            <>
              <TextInput
                label="SMTP Host"
                placeholder="smtp.example.com"
                {...form.getInputProps("oauth_config.smtp_host")}
              />

              <NumberInput
                label="SMTP Port"
                placeholder="587"
                min={1}
                max={65_535}
                {...form.getInputProps("oauth_config.smtp_port")}
              />
            </>
          )}

          {/* Help text */}
          <Text size="xs" c="dimmed">
            Após criar a conta, você será redirecionado para autorizar via OAuth2. Certifique-se de ter configurado as
            credenciais OAuth2 no console do provider.
          </Text>

          {/* Action buttons */}
          <Group justify="flex-end">
            <Button variant="subtle" onClick={handleClose} disabled={submitting || loading}>
              Cancelar
            </Button>
            <Button type="submit" loading={submitting || loading}>
              Criar e Autorizar
            </Button>
          </Group>
        </Stack>
      </form>
    </Modal>
  );
}

CreateEmailAccountModal.displayName = "CreateEmailAccountModal";
